// apps/web/src/people/WorkloadView.tsx
//
// Coverage-first Workload board: Hotel > Reparto > Persone.
// - Coverage row per department (green/amber/red vs required staffing).
// - "Where" is implicit in the grouping; cells show slot + draft/published.
// - Per-person CCNL badges (over-allocation hours, consecutive nights).
// - No silent cap: an honest "shown / total" footer.
//
// All grouping/coverage/away-day/badge math lives in the pure, unit-tested
// `buildBoardModel` (./workload-board); this file is presentation only.
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { getUsersApi, listHotels, peopleClient, useCore } from "@praesto/core";
import type { WorkloadBoard, LeaveType } from "@praesto/shared-types";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Toolbar,
  Input,
  Select,
  Button,
  Badge,
  SkeletonTableRows,
  EmptyState,
  AvatarOrInitials,
  PageFull,
  departmentName,
} from "@praesto/ui-kit";
import {
  buildBoardModel,
  type BoardCell,
  type CoverageCell,
  type BoardRow,
} from "./workload-board";

const SLOT_COLOR: Record<"M" | "P" | "N" | "X", string> = {
  M: "bg-info-soft text-info",
  P: "bg-warning-soft text-warning",
  // Night is an inversion INSIDE a theme-following surface: theme tokens
  // (bg-inverse/fg-inverse) swap by themselves. Rest sits on the opaque
  // bg-subtle — hover/active veils never host text.
  N: "bg-inverse text-fg-inverse",
  X: "bg-subtle text-fg-muted",
};
const SLOT_LABEL: Record<"M" | "P" | "N" | "X", string> = {
  M: "Mattino",
  P: "Pomeriggio",
  N: "Notte",
  X: "Riposo",
};
const LEAVE_CODE: Record<LeaveType, { code: string; cls: string; label: string }> = {
  vacation: { code: "FE", cls: "bg-success-soft text-success", label: "Ferie" },
  permit: { code: "PE", cls: "bg-info-soft text-info", label: "Permesso" },
  sick: { code: "MA", cls: "bg-danger-soft text-danger", label: "Malattia" },
  // A statutory leave shows on the rota like any other absence — the planner must
  // see that the person is away — but neutrally, and without naming the reason.
  leave: { code: "CO", cls: "bg-info-soft text-info", label: "Congedo" },
};
const COVERAGE_CLS: Record<CoverageCell["level"], string> = {
  ok: "text-success",
  warn: "text-warning",
  bad: "text-danger font-semibold",
  unknown: "text-fg-muted",
};
const COVERAGE_WORD: Record<CoverageCell["level"], string> = {
  ok: "coperto",
  warn: "al minimo",
  bad: "scoperto",
  unknown: "—",
};
const DAYS_SHORT = ["L", "M", "M", "G", "V", "S", "D"];

function mondayOf(d: Date): string {
  const dd = new Date(d);
  const dow = dd.getUTCDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  dd.setUTCDate(dd.getUTCDate() + diff);
  return dd.toISOString().slice(0, 10);
}
function addDaysISO(date: string, n: number): string {
  const [y, m, day] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, day! + n)).toISOString().slice(0, 10);
}
function dayIndex(date: string): number {
  const d = new Date(`${date}T00:00:00Z`).getUTCDay();
  return (d + 6) % 7; // Mon=0
}

interface HotelOpt {
  id: string;
  name: string;
}

export function WorkloadView() {
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [weekStart, setWeekStart] = useState<string>(() => mondayOf(new Date()));
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const [search, setSearch] = useState("");
  const [hotelId, setHotelId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [hotels, setHotels] = useState<HotelOpt[]>([]);
  const [board, setBoard] = useState<WorkloadBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Hotel filter options.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await listHotels(api.apiBase, tok);
      if (!cancelled) setHotels(r.hotels.map((h) => ({ id: h.id, name: h.name })));
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Board data.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    peopleClient
      .getWorkloadBoard(weekStart, days, hotelId || undefined)
      .then((b) => {
        if (!cancelled) setBoard(b);
      })
      .catch(() => {
        if (!cancelled) setBoard(null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [weekStart, days, hotelId]);

  const model = useMemo(
    () => (board ? buildBoardModel(board, weekStart, days, { search, departmentId }) : null),
    [board, weekStart, days, search, departmentId],
  );

  const dayDates = useMemo(
    () => Array.from({ length: days }, (_, i) => addDaysISO(weekStart, i)),
    [weekStart, days],
  );

  const departmentOptions = useMemo(() => {
    if (!board) return [] as Array<{ id: string; name: string }>;
    return board.departments
      .filter((d) => !hotelId || d.hotelId === hotelId)
      .map((d) => ({ id: d.id, name: departmentName(d) }));
  }, [board, hotelId]);

  function toggle(key: string): void {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const fmtDate = (d: string): string => {
    const [, m, day] = d.split("-");
    return `${day}/${m}`;
  };

  return (
    <PageFull>
      <Toolbar
        title="Carichi di lavoro"
        actions={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center text-sm">
            <div className="flex items-center gap-2">
              <Button
                size="md"
                variant="ghost"
                className="tap-target px-3"
                aria-label={core.i18n.t("people.nav.period_prev")}
                onClick={() => setWeekStart(addDaysISO(weekStart, -days))}
              >
                <ChevronLeft size={16} aria-hidden />
              </Button>
              <Button
                size="md"
                variant="secondary"
                className="tap-target"
                onClick={() => setWeekStart(mondayOf(new Date()))}
              >
                Oggi
              </Button>
              <Button
                size="md"
                variant="ghost"
                className="tap-target px-3"
                aria-label={core.i18n.t("people.nav.period_next")}
                onClick={() => setWeekStart(addDaysISO(weekStart, days))}
              >
                <ChevronRight size={16} aria-hidden />
              </Button>
            </div>
            <div className="flex items-center justify-between gap-2 sm:justify-start">
              <span className="whitespace-nowrap text-mono-sm text-fg-muted">
                {weekStart} → +{days}gg
              </span>
              <span className="w-24 shrink-0">
                <Select
                  size="sm"
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value) as 7 | 14 | 30)}
                >
                  <option value={7}>7gg</option>
                  <option value={14}>14gg</option>
                  <option value={30}>30gg</option>
                </Select>
              </span>
            </div>
          </div>
        }
        filters={
          <>
            <Input
              size="md"
              placeholder="Cerca persona…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              size="md"
              value={hotelId}
              onChange={(e) => {
                setHotelId(e.target.value);
                setDepartmentId("");
              }}
            >
              <option value="">Tutti gli hotel</option>
              {hotels.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
            <Select
              size="md"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
            >
              <option value="">Tutti i reparti</option>
              {departmentOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </>
        }
      />

      {loading && <SkeletonTableRows rows={6} cols={9} />}

      {!loading && model && model.hotels.length === 0 && (
        <EmptyState
          title="Nessun turno"
          description="Nessun turno pianificato nel periodo coi filtri attivi."
          size="compact"
        />
      )}

      {/* The board still renders — people, departments and coverage gaps are
          all meaningful with an empty schedule — but says why every cell is
          blank. Without this, a week nobody has planned yet is indistinguishable
          from a page that failed to load. */}
      {!loading && model && model.hotels.length > 0 && board?.shifts.length === 0 && (
        <div className="mb-3 px-3 py-2 rounded-md bg-elevated border border-border-default text-caption text-fg-muted">
          Nessun turno pianificato in questa settimana: la copertura è calcolata sui modelli di
          turno, le persone elencate sono quelle assegnate ai reparti.
        </div>
      )}

      {!loading &&
        model &&
        model.hotels.map((hotel) => {
          const hotelKey = `h:${hotel.id}`;
          const hotelOpen = !collapsed.has(hotelKey);
          return (
            <div key={hotel.id} className="mb-4">
              <button
                type="button"
                aria-expanded={hotelOpen}
                onClick={() => toggle(hotelKey)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-elevated border border-border-default text-left transition-colors duration-fast hover:bg-subtle"
              >
                <span className="text-fg-muted">
                  {hotelOpen ? (
                    <ChevronDown size={14} aria-hidden />
                  ) : (
                    <ChevronRight size={14} aria-hidden />
                  )}
                </span>
                <span className="font-medium">{hotel.name}</span>
                <span className="whitespace-nowrap text-mono-xs text-fg-muted">
                  {hotel.departments.length} reparti
                </span>
                {hotel.gaps > 0 && (
                  <span className="ml-auto">
                    <Badge tone="danger" size="xs">
                      {hotel.gaps} {hotel.gaps === 1 ? "buco" : "buchi"} copertura
                    </Badge>
                  </span>
                )}
              </button>

              {hotelOpen &&
                hotel.departments.map((dept) => {
                  const deptKey = `d:${hotel.id}:${dept.id}`;
                  const deptOpen = !collapsed.has(deptKey);
                  return (
                    <div
                      key={dept.id}
                      className="mt-2 ml-0 sm:ml-3 border border-border-default rounded-md overflow-hidden"
                    >
                      <button
                        type="button"
                        aria-expanded={deptOpen}
                        onClick={() => toggle(deptKey)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 bg-subtle text-left text-sm transition-colors duration-fast hover:bg-hover"
                      >
                        <span className="text-fg-muted">
                          {deptOpen ? (
                            <ChevronDown size={14} aria-hidden />
                          ) : (
                            <ChevronRight size={14} aria-hidden />
                          )}
                        </span>
                        <span className="font-medium text-fg-default">{dept.name}</span>
                        <span className="whitespace-nowrap text-mono-xs text-fg-muted">
                          {dept.rows.length} {dept.rows.length === 1 ? "persona" : "persone"}
                        </span>
                      </button>

                      {deptOpen && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-mono-xs">
                            <thead>
                              <tr className="bg-elevated">
                                <th className="sticky left-0 z-10 bg-elevated text-left p-2 border-r border-border-default min-w-[150px] max-w-[45vw]">
                                  Persona
                                </th>
                                {dayDates.map((d) => (
                                  <th
                                    key={d}
                                    className="p-1 text-center border-l border-border-default min-w-[34px]"
                                  >
                                    {DAYS_SHORT[dayIndex(d)]}
                                    <br />
                                    <span className="text-fg-muted">{fmtDate(d).slice(0, 5)}</span>
                                  </th>
                                ))}
                                <th
                                  className="p-1 border-l border-border-default min-w-[36px]"
                                  title="Ore in questo reparto"
                                >
                                  h
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <CoverageRow
                                deptName={dept.name}
                                coverage={dept.coverage}
                                fmtDate={fmtDate}
                              />
                              {dept.rows.map((row) => (
                                <PersonRow
                                  key={row.userId}
                                  row={row}
                                  dayDates={dayDates}
                                  deptName={dept.name}
                                  tenantSlug={tenantSlug}
                                  fmtDate={fmtDate}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}

      {!loading && model && (
        <div className="mt-2 text-caption text-fg-muted">
          Mostrate {model.shownPersons} / {model.totalPersons} persone
          {model.truncated && (
            <span className="ml-2 text-danger">
              · dati troncati: alza i filtri per vedere tutto
            </span>
          )}
        </div>
      )}

      <Legend />
    </PageFull>
  );
}

function CoverageRow({
  deptName,
  coverage,
  fmtDate,
}: {
  deptName: string;
  coverage: CoverageCell[];
  fmtDate: (d: string) => string;
}) {
  return (
    <tr className="border-t border-border-default bg-subtle">
      <td className="sticky left-0 z-10 bg-subtle p-2 border-r border-border-default text-fg-muted text-xs">
        Copertura
      </td>
      {coverage.map((c) => (
        <td
          key={c.date}
          className={`text-center whitespace-nowrap border-l border-border-default ${COVERAGE_CLS[c.level]}`}
          title={`${deptName}, ${fmtDate(c.date)}: ${c.assigned}${
            c.required !== null ? ` di ${c.required}` : ""
          } — ${COVERAGE_WORD[c.level]}`}
        >
          {c.required !== null && c.level !== "ok" ? `${c.assigned}/${c.required}` : c.assigned}
        </td>
      ))}
      <td className="border-l border-border-default" />
    </tr>
  );
}

function BoardCellView({
  cell,
  who,
  date,
  fmtDate,
}: {
  cell: BoardCell;
  who: string;
  date: string;
  fmtDate: (d: string) => string;
}) {
  if (cell.kind === "shift") {
    const draft = cell.status === "draft";
    return (
      <td
        className={`relative text-center border-l border-border-default min-w-[34px] ${
          SLOT_COLOR[cell.slot]
        } ${draft ? "border border-dashed border-border-strong" : ""}`}
        title={`${who} · ${fmtDate(date)} · ${SLOT_LABEL[cell.slot]} · ${
          draft ? "bozza" : "pubblicato"
        }`}
      >
        {cell.slot}
        {draft && (
          <span className="absolute top-0 right-0.5 text-mono-xs" aria-hidden>
            ·
          </span>
        )}
      </td>
    );
  }
  if (cell.kind === "away") {
    return (
      <td
        className="text-center border-l border-border-default text-fg-muted text-mono-xs leading-tight"
        title={`${who} · ${fmtDate(date)} · in ${cell.departmentName}`}
      >
        →{cell.departmentName.slice(0, 4)}
      </td>
    );
  }
  if (cell.kind === "leave") {
    const lv = LEAVE_CODE[cell.leaveType];
    return (
      <td
        className={`text-center border-l border-border-default text-mono-xs font-semibold ${lv.cls}`}
        title={`${who} · ${fmtDate(date)} · ${lv.label}`}
      >
        {lv.code}
      </td>
    );
  }
  return (
    <td className="text-center border-l border-border-default bg-elevated text-fg-muted">·</td>
  );
}

function PersonRow({
  row,
  dayDates,
  deptName,
  tenantSlug,
  fmtDate,
}: {
  row: BoardRow;
  dayDates: string[];
  deptName: string;
  tenantSlug: string;
  fmtDate: (d: string) => string;
}) {
  const hCls =
    row.deltaSign === "over"
      ? "text-danger font-medium"
      : row.deltaSign === "low"
        ? "text-info"
        : "text-fg-default";
  return (
    <tr className="border-t border-border-default">
      <td className="sticky left-0 z-10 bg-elevated p-2 border-r border-border-default max-w-[45vw]">
        <div className="flex items-center gap-1.5 min-h-[32px]">
          <Link
            to={`/${tenantSlug}/people/${row.userId}/info` as never}
            className="flex items-center gap-1.5 hover:underline min-w-0"
          >
            <AvatarOrInitials
              displayName={row.displayName}
              size={24}
              {...(row.photoUrl ? { photoUrl: row.photoUrl } : {})}
            />
            <span className="truncate">{row.displayName}</span>
          </Link>
          {row.badges.overHours !== undefined && (
            <Badge tone="danger" size="xs">
              {row.badges.overHours}h
            </Badge>
          )}
          {row.badges.consecutiveNights !== undefined && (
            <Badge tone="warning" size="xs">
              {row.badges.consecutiveNights} notti
            </Badge>
          )}
        </div>
      </td>
      {row.cells.map((cell, i) => (
        <BoardCellView
          key={dayDates[i]}
          cell={cell}
          who={`${row.displayName} · ${deptName}`}
          date={dayDates[i]!}
          fmtDate={fmtDate}
        />
      ))}
      <td className={`text-center whitespace-nowrap border-l border-border-default ${hCls}`}>
        {row.hoursInGroup || "·"}
      </td>
    </tr>
  );
}

function Legend() {
  return (
    <div className="mt-4 text-mono-xs text-fg-muted flex flex-wrap gap-2">
      <span className="px-2 py-0.5 bg-info-soft text-info rounded-xs">M mattino</span>
      <span className="px-2 py-0.5 bg-warning-soft text-warning rounded-xs">P pomeriggio</span>
      <span className="px-2 py-0.5 bg-inverse text-fg-inverse rounded-xs">N notte</span>
      <span className="px-2 py-0.5 bg-subtle text-fg-muted rounded-xs">X riposo</span>
      <span className="px-2 py-0.5 border border-dashed border-border-strong rounded-xs">
        bozza ·
      </span>
      <span className="px-2 py-0.5 bg-success-soft text-success rounded-xs">FE ferie</span>
      <span className="px-2 py-0.5 bg-info-soft text-info rounded-xs">PE permesso</span>
      <span className="px-2 py-0.5 bg-danger-soft text-danger rounded-xs">MA malattia</span>
      <span className="px-2 py-0.5 bg-danger-soft text-danger rounded-xs">Nh oltre contratto</span>
      <span className="px-2 py-0.5 bg-warning-soft text-warning rounded-xs">notti consecutive</span>
    </div>
  );
}
